import { tokenRefreshManager } from "@/lib/tokenRefresh";
import Cookies from "@/lib/useCookies";
import AuthAPI from "@/services/api-calls/auth.api";
import { isMobileDeepLink, sanitizeRedirectTo } from "@/utils/redirect.util";
import React, { useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useSearchParams } from "react-router-dom";

const PublicMiddleware: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isValidating, setIsValidating] = useState(true);
  const [searchParams] = useSearchParams();
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    const validateAuth = async () => {
      try {
        const token = await tokenRefreshManager.getValidAccessToken();

        if (token) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Auth validation error:", error);
        setIsAuthenticated(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateAuth();
  }, []);

  useEffect(() => {
    if (isValidating || !isAuthenticated || hasRedirectedRef.current) return;

    const redirectTo = sanitizeRedirectTo(searchParams.get("redirectTo"));
    if (!redirectTo || !isMobileDeepLink(redirectTo)) return;

    hasRedirectedRef.current = true;

    (async () => {
      try {
        // Re-validate against the server before handing tokens back to the
        // mobile app — a locally-unexpired cookie may belong to a session the
        // backend already revoked (e.g. the user logged out on mobile).
        await AuthAPI.getCurrentUser();

        // Hand the current session's tokens back to the mobile app via its
        // deep-link callback. The access token is refreshed if stale; the
        // refresh token was persisted at login.
        const accessToken = await tokenRefreshManager.getValidAccessToken();
        const refreshToken = Cookies.getCookie<string>("refreshToken");
        const url = new URL(redirectTo);
        if (accessToken) url.searchParams.set("accessToken", accessToken);
        if (refreshToken) url.searchParams.set("refreshToken", refreshToken);
        window.location.href = url.toString();
      } catch {
        // Server says the session is dead — clear local state so the login
        // form is shown instead of bouncing back with stale tokens.
        tokenRefreshManager.clearAuth();
        Cookies.deleteAllCookies();
        setIsAuthenticated(false);
      }
    })();
  }, [isValidating, isAuthenticated, searchParams]);

  if (isValidating || isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-2 text-sm text-muted-foreground">Validating session...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    const redirectTo = sanitizeRedirectTo(searchParams.get("redirectTo"));
    if (redirectTo) {
      if (isMobileDeepLink(redirectTo)) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-2 text-sm text-muted-foreground">Redirecting...</p>
            </div>
          </div>
        );
      }
      if (/^https?:\/\//i.test(redirectTo)) {
        window.location.href = redirectTo;
        return (
          <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-2 text-sm text-muted-foreground">Redirecting...</p>
            </div>
          </div>
        );
      }
      return <Navigate to={redirectTo} replace />;
    }
    return <Navigate to="/inbox" replace />;
  }

  return <Outlet />;
};

export default PublicMiddleware;
